"use client";

import { useState } from "react";
import { Box, Popover, Typography } from "@mui/material";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import VERTICAL_OPTIONS from "@/constants/VerticalOptions";
import { FlatAspect } from "@/models/HomePageSearchResponse";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { usePageBackdrop } from "./usePageBackdrop";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { usePreviousRouteIsSearch } from "./usePreviousRouteIsSearch";

function MenuOptionsList({
  options,
  large,
  selectedOption,
}: {
  options: FlatAspect[];
  large?: boolean;
  selectedOption?: FlatAspect;
}) {
  return (
    <MenuList>
      {options.map((option) => (
        <MenuItem
          key={option.id}
          isSelected={selectedOption?.id === option.id}
          href={`/for-sale/${option.slug}`}
          isLarge={large}
          label={option.label}
        />
      ))}
    </MenuList>
  );
}

function MenuList({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="ul"
      sx={{
        listStyle: "none",
        display: "flex",
        columnGap: 5,
        flexDirection: "column",
        "a:hover": {
          textDecoration: "underline",
        },
        maxHeight: "500px",
        flexWrap: "wrap",
      }}
    >
      {children}
    </Box>
  );
}

function MenuItem({
  href = "",
  label,
  isSelected,
  isLarge,
  onClick,
}: {
  href?: string;
  label: string;
  isSelected?: boolean;
  isLarge?: boolean;
  blockClick?: boolean;
  onClick?: () => void;
}) {
  return (
    <Typography
      component="li"
      sx={(theme) => ({
        fontWeight: isSelected ? "bold" : "initial",
        a: {
          paddingTop: isLarge ? 1 : 0.5,
          paddingBottom: isLarge ? 1 : 0.5,
          display: "block",
          fontSize: isLarge ? theme.typography.h6.fontSize : "inherit",
        },
      })}
    >
      <Link
        href={href}
        onClick={(e) => {
          if (onClick) {
            e.preventDefault();
            e.nativeEvent.stopImmediatePropagation();
            onClick();
          }
        }}
      >
        {label}
      </Link>
    </Typography>
  );
}

export function BuyMenu() {
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<null | HTMLElement>();
  const { setBackdropOpen } = usePageBackdrop();
  const pathName = usePathname();
  const router = useRouter();
  const previousPathIsSearch = usePreviousRouteIsSearch();
  const verticalSlug = pathName.split("/")[2];
  const selectedVertical = VERTICAL_OPTIONS.find(
    (x) => x.slug === verticalSlug,
  );
  const isDetailsPage = pathName.startsWith("/for-sale/details/");

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setPopoverAnchorEl(event.currentTarget);
    setBackdropOpen(true);
  };

  const handlePopoverClose = () => {
    setPopoverAnchorEl(null);
    setBackdropOpen(false);
  };

  const isBuyMenuOpen = Boolean(popoverAnchorEl);

  return (
    <>
      <Box
        sx={{
          display: "flex",
        }}
        onClick={(event) => {
          if (!isDetailsPage) {
            event.preventDefault();
            event.stopPropagation();
            event.nativeEvent.stopImmediatePropagation();
            if (popoverAnchorEl) {
              handlePopoverClose();
            } else {
              handlePopoverOpen(event);
            }
          } else if (previousPathIsSearch) {
            event.preventDefault();
            event.stopPropagation();
            event.nativeEvent.stopImmediatePropagation();
            router.back();
          }
        }}
      >
        {isDetailsPage ? (
          <>
            {/* AJB: navigate back where possible, or just relabel everything equine */}
            <NavigateBeforeIcon
              sx={{
                marginLeft: -1,
              }}
            />
            <Typography>Back</Typography>
          </>
        ) : (
          <>
            <Typography
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: "inherit",
              }}
            >
              {selectedVertical?.label ?? "Buy"}
            </Typography>
            <ExpandMoreIcon />
          </>
        )}
      </Box>
      <Popover
        id="mouse-over-popover"
        open={isBuyMenuOpen}
        anchorEl={popoverAnchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
        sx={{
          cursor: "pointer",
        }}
      >
        <Box
          sx={{
            padding: 3,
            display: "flex",
            gap: 5,
          }}
          // AJB: is the mouse leave quirky?
          onMouseLeave={handlePopoverClose}
          onClick={handlePopoverClose}
        >
          <MenuOptionsList
            options={VERTICAL_OPTIONS}
            large
            selectedOption={selectedVertical}
          />
        </Box>
      </Popover>
    </>
  );
}
